import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { Button as LyfeButton } from "@lyfegen-platform/react-common";
import { Card, useTheme } from "@material-ui/core";
import { useField } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import useStyles from "../../containers/data/forms/forms-styles";
import { FlexSectionController } from "../../models/flexible/flex-section-controller";
import { createSingleSectionInstanceInitialValues } from "../../models/flexible/initial-values";
import { isSectionContaining } from "../../models/flexible/utils";
import { flexibleFormResponseState } from "../../store/flexible";
import { firstLetterUppercase } from "../../utils/first-letter-uppercase";
import { SectionBody } from "./SectionBody";

export function SectionArray(props: { controller: FlexSectionController; path: string }) {
	const [sectionDataArray, { initialValue }, helpers] = useField<Array<any>>(props.path);
	const [rowInfos, setRowInfos] = useState<Array<{ initialIndex?: number; isDisabled: boolean }>>(
		initialValue?.map((item, index) => ({
			initialIndex: index,
			isDisabled: props.controller.context.impossibleToEditAttributes?.some(
				({ identifier, row }) => identifier in item && row === index
			),
		})) || []
	);
	const initialData = useRecoilValue(flexibleFormResponseState);
	const styles = useStyles();
	const theme = useTheme();
	const { t } = useTranslation();
	const canAddItem = props.controller.useCanAddItem ? props.controller.useCanAddItem() : true;
	const canCopy =
		canAddItem && isSectionContaining(props.controller, AttributeIdentifiers.PrescriptionDate, AttributeIdentifiers.AdministrationDate);
	const canDeleteItem = props.controller.useCanDeleteItem ? props.controller.useCanDeleteItem(props.path) : true;
	const handleDeleteRow = props.controller.useOnRowDeleted?.();

	function addNewRow() {
		setRowInfos([...rowInfos, { isDisabled: false }]);
		helpers.setValue([...sectionDataArray.value, createSingleSectionInstanceInitialValues(props.controller, {}, initialData)]);
	}

	function handleCopy(index: number) {
		const items = [...sectionDataArray.value];
		items.splice(index + 1, 0, items[index]);
		rowInfos.splice(index + 1, 0, { isDisabled: false });
		setRowInfos(rowInfos);
		helpers.setValue(items);
	}

	function handleDelete(index: number) {
		setRowInfos(rowInfos.filter((_, i) => i !== index));
		helpers.setValue(sectionDataArray.value.filter((_, i) => i !== index));
		handleDeleteRow?.();
	}

	return (
		<>
			{sectionDataArray.value.map((_, i) => (
				<Card key={i} style={{ margin: theme.spacing(2.5, 0), boxShadow: "none" }}>
					<SectionBody
						controller={props.controller}
						parentPath={props.path}
						sectionPath={`${props.path}.${i}`}
						canDelete={
							props.controller.context.isEditable &&
							sectionDataArray.value.length > 1 &&
							canDeleteItem &&
							!rowInfos[i]?.isDisabled
						}
						canCopy={props.controller.context.isEditable && canCopy}
						initialIndex={rowInfos[i]?.initialIndex}
						handleCopy={() => handleCopy(i)}
						handleDelete={() => handleDelete(i)}
					/>
				</Card>
			))}
			{props.controller.context.isEditable && canAddItem && (
				<LyfeButton className={styles.formButton} use="add" color="primary" onClick={addNewRow}>
					{firstLetterUppercase(t(`add-${props.controller.schema.localizableKey}`))}
				</LyfeButton>
			)}
		</>
	);
}
